import { useState } from 'react';
import { verifyPayment, generateMerchantTransactionId } from '../services/paymentsHubService';
import type { AuthorizePayVerifyRequest } from '../services/paymentsHubService';
import { useTranslation } from '../i18n/translations';

export interface VerifyAndEnrollParams {
  token: string;
  tokenIssuer?: string;
  tokenType?: string;
  firstName?: string;
  lastName?: string;
  postalCode?: string;
  country?: string;
  memberCardNumber?: string;
  legacyAccountId?: string;
  locationNumber: string;
  userId?: string;
}

export function useVerifyAndEnroll() {
  const { t } = useTranslation();
  const [verifying, setVerifying] = useState(false);
  const [verifyResponse, setVerifyResponse] = useState<any>(null);
  const [verifyError, setVerifyError] = useState<string | null>(null);

  const verify = async (params: VerifyAndEnrollParams, authToken?: string) => {
    setVerifying(true);
    setVerifyError(null);
    setVerifyResponse(null);

    try {
      const req: AuthorizePayVerifyRequest = {
        merchantTransactionId: generateMerchantTransactionId(),
        locationNumber: params.locationNumber,
        sourceOfFunds: {
          fundingSourceType: 'PaymentAccount',
          paymentAccount: {
            type: 'Card',
            tokens: [
              {
                tokenIssuer: params.tokenIssuer || 'Cybersource',
                tokenType: params.tokenType || 'CybersourceFlexTransientToken',
                tokenValue: params.token || ''
              }
            ],
            accountHolderInformation: {
              firstName: params.firstName || '',
              lastName: params.lastName || ''
            },
            billingAddress: {
              postalCode: params.postalCode,
              country: params.country
            }
          }
        }
      };

      // Retry logic for verify-and-enroll (up to 3 attempts)
      const maxRetries = 3;
      let lastError: any = null;
      let vr: any = null;
      let isDeclined = false;

      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
          vr = await verifyPayment(
            req,
            {
              memberCardNumber: params.memberCardNumber,
              legacyAccountId: params.legacyAccountId,
              country: params.country?.toUpperCase(),
              userId: params.userId
            },
            authToken
          );

          const resultCode = vr?.verifyResponse?.paymentsHubTransactionResultCode;

          // Check if the card was declined (do not retry)
          if (resultCode === 'Declined') {
            isDeclined = true;
            const declineReason = vr?.verifyResponse?.responses?.[0]?.gatewayMessage ||
                                  vr?.verifyResponse?.responses?.[0]?.gatewayCode ||
                                  'Card declined';
            lastError = new Error(declineReason);
            break; // Exit retry loop
          }

          // Check if the response indicates a failure (e.g., backend verify call failed)
          if (resultCode === 'Failure') {
            const errorMsg = `Backend verify failed: ${vr?.verifyResponse?.responses?.[0]?.gatewayCode || 'Unknown error'}`;
            lastError = new Error(errorMsg);
            console.warn(`Verify attempt ${attempt}/${maxRetries} returned Failure:`, errorMsg);

            // Reset vr so we retry
            vr = null;

            // If this is not the last attempt, wait briefly before retrying
            if (attempt < maxRetries) {
              await new Promise(resolve => setTimeout(resolve, 500 * attempt)); // Exponential backoff: 500ms, 1000ms
            }
          } else {
            // Success! Break out of retry loop
            break;
          }
        } catch (ex: any) {
          lastError = ex;
          console.warn(`Verify attempt ${attempt}/${maxRetries} failed:`, ex.message);

          // If this is not the last attempt, wait briefly before retrying
          if (attempt < maxRetries) {
            await new Promise(resolve => setTimeout(resolve, 500 * attempt)); // Exponential backoff: 500ms, 1000ms
          }
        }
      }

      setVerifying(false);

      // Check if card was declined
      if (isDeclined) {
        setVerifyError(t('cardDeclined'));
        return { success: false, declined: true };
      }

      // Check if we succeeded
      if (vr) {
        const resultCode = vr?.verifyResponse?.paymentsHubTransactionResultCode;
        const autoRenewStatus = vr?.autoRenew?.status;
        const autoRenewError = vr?.autoRenew?.errors?.message;

        // PayHub Success BUT auto-renew enrollment failed
        if (resultCode === 'Success' && autoRenewStatus === 'ERROR') {
          const translatedMessage = t('autoRenewEnrollmentFailed');
          const errorDetails = autoRenewError ? `\n${autoRenewError}` : '';
          setVerifyError(translatedMessage + errorDetails);
          return { success: false, enrollmentFailed: true };
        }

        // Both PayHub Success AND auto-renew Success
        if (resultCode === 'Success' && autoRenewStatus === 'SUCCESS') {
          setVerifyResponse(vr);
          return { success: true, response: vr };
        }

        // PayHub failure
        setVerifyError(t('unableToVerifyPayment'));
        return { success: false };
      } else {
        // All retries failed
        setVerifyError(t('unableToVerifyPayment'));
        return { success: false };
      }
    } catch (e: any) {
      setVerifying(false);
      const translatedMessage = t('tokenizationFailed');
      const errorDetails = e.message ? `\n${e.message}` : '';
      setVerifyError(translatedMessage + errorDetails);
      return { success: false, error: e };
    }
  };

  const resetState = () => {
    setVerifyResponse(null);
    setVerifyError(null);
  };

  return {
    verify,
    verifying,
    verifyResponse,
    verifyError,
    resetState
  };
}
